/**
 * Instrumentation for the selector layer, active **only under tests** - which includes the
 * benchmarks in `test/bench`. See the gate in `createImmutableEqualSelector.ts`: no served build
 * pays for this, localhost and QA included, because `captureDefinition` takes a stack per selector
 * at module load whether or not anything is recording.
 *
 * Two things are recorded, both from one wrapper around selectors made by
 * `createImmutableEqualSelector`:
 *
 * - **Wasted deep walks.** That creator compares memoised dependencies with `Immutable.is`,
 *   which walks entry by entry whenever two instances hold equal values but differ by
 *   identity. Such a walk always concludes "nothing changed", so its entire cost is waste.
 *   In a routes-overview search profile it was 182 ms of a 407 ms task.
 * - **Selector calls per interaction.** Which selectors run, and how often, across a page
 *   render, a pagination step or a websocket flush.
 *
 * Recording is off until started, so the standing cost is one boolean check per selector call.
 * Wrap the phase you care about:
 *
 *     glowSelectorStats.start('pagination')   // then drive the thing
 *     glowSelectorStats.report()              // print, keep recording
 *     glowSelectorStats.stop()                // print and stop
 *
 * `stop` and `report` also return the rows, and `snapshot` gives them without printing, so a
 * benchmark can assert on them rather than leaving someone to read a table.
 */
import { is } from 'immutable'

export interface SelectorStats {
  definedAt: string
  /**
   * The raw stack frame, with an absolute path. Read the **file** from either this or
   * `definedAt`; do not trust the **line**. The label is reconstructed from a stack captured at
   * module load, after the transform has moved the code - `consignmentIdsForSlotIdSelector`, on
   * line 422 of its file, reports as 326 - and the offset differs per file.
   */
  frame: string
  calls: number
  equalityChecks: number
  wastedWalks: number
  wastedMs: number
  walkedEntries: number
}

const statsByDefinition = new Map<string, SelectorStats>()

let currentStats: SelectorStats | null = null
let recording = false
let recordingLabel = ''

const statsFor = (definedAt: string, frame: string): SelectorStats => {
  const existing = statsByDefinition.get(definedAt)
  if (existing) return existing
  const created: SelectorStats = {
    definedAt,
    frame,
    calls: 0,
    equalityChecks: 0,
    wastedWalks: 0,
    wastedMs: 0,
    walkedEntries: 0
  }
  statsByDefinition.set(definedAt, created)
  return created
}

// Reading `.size` does not force a lazy Seq, where it is undefined. Never call .count() here.
const sizeOf = (value: unknown): number => {
  const size = (value as { size?: unknown } | null | undefined)?.size
  return typeof size === 'number' ? size : 0
}

/**
 * Equality check standing in for `Immutable.is`. The `a === b` short circuit is Immutable's
 * own first line; it is repeated here so the cheap path is never timed and only genuine
 * structural walks are attributed.
 */
export const instrumentedIs = (a: unknown, b: unknown): boolean => {
  if (!recording) return is(a, b)

  const stats = currentStats
  if (stats) stats.equalityChecks++
  if (a === b) return true

  const startedAt = performance.now()
  const equal = is(a, b)

  if (equal && stats) {
    stats.wastedWalks++
    stats.wastedMs += performance.now() - startedAt
    stats.walkedEntries += sizeOf(a)
  }
  return equal
}

/**
 * A "<file>:<line>:<col>" tail, with or without wrapping parens, and tolerating the query
 * string Vite appends in dev. Deliberately not restricted to .ts/.tsx: a bundled QA build
 * reports chunk filenames, and a position inside a chunk still identifies a selector even
 * though it does not name one.
 */
const SITE = /([^/\\()\s]+?)(?:\?[^:\s)]*)?:(\d+):(\d+)\)?$/

const INTERNAL = /selectorInstrumentation|createImmutableEqualSelector|captureDefinitionSite/

/**
 * Where a selector was defined, read off the creation stack at module load.
 *
 * The label is a convenience, not a source of truth: the dev server strips type-only imports
 * without preserving line numbers, so a reported line sits above the real one by however many
 * lines of types the file's import block held. The raw frame is kept alongside it because the
 * console sourcemaps that correctly when clicked.
 *
 * Stack formats vary more than is comfortable to predict, so a failure to parse reports the
 * raw frames rather than "unknown" — the text of the frame is what tells us how to parse it.
 */
export interface SelectorDefinition {
  /** Short label for grouping and reading, e.g. "routePointsSelector.ts:38". */
  site: string
  /** The frame it came from, verbatim, so the console can sourcemap it. */
  frame: string
}

const parseStack = (stack: string | undefined): SelectorDefinition => {
  if (!stack) return { site: 'unparsed: no stack available', frame: '' }

  const frames = stack
    .split('\n')
    .slice(1)
    .map((line) => line.trim().replace(/^at\s+/, ''))
    .filter(Boolean)

  // Drop our own frames by name. If names are mangled nothing is dropped, and we cannot then
  // tell which frames are ours - so keep them all rather than guessing at an offset that
  // minification may have changed.
  const external = frames.filter((frame) => !INTERNAL.test(frame))
  const candidates = external.length < frames.length ? external : frames

  const positions: string[] = []
  let firstParsed = ''

  for (const frame of candidates) {
    const match = SITE.exec(frame)
    if (!match) continue
    if (!firstParsed) firstParsed = frame

    const [, file, line, column] = match
    // A source filename names the selector outright, which is the whole point
    if (/\.tsx?$/.test(file)) return { site: `${file}:${line}`, frame }
    positions.push(`${file}:${line}:${column}`)
  }

  // No source frame, so this is a bundled build where our own frames cannot be filtered out
  // by name and minification may have inlined some away. A single position is then often one
  // the whole chunk shares; two of them differ per selector, which is what the ranking needs.
  if (positions.length > 0) return { site: positions.slice(0, 2).join(' <- '), frame: firstParsed }

  return {
    site: `unparsed: ${candidates.slice(0, 3).join(' <- ') || frames[0] || 'empty stack'}`,
    frame: frames[0] ?? ''
  }
}

/** The label alone. Separated from the stack capture so the parsing can be tested. */
export const captureDefinitionSite = (stack = new Error('selector definition site').stack): string =>
  parseStack(stack).site

/** Label and raw frame, for the selector creator to record. */
export const captureDefinition = (stack = new Error('selector definition site').stack): SelectorDefinition =>
  parseStack(stack)

/**
 * Counts calls, and makes this selector the attribution target for any equality check that
 * runs inside it. Saved and restored rather than assigned, because selectors compose.
 */
export const instrumentSelector = <F extends (...args: never[]) => unknown>(
  selector: F,
  definedAt: string,
  frame = definedAt
): F => {
  const stats = statsFor(definedAt, frame)

  const instrumented = (...args: never[]) => {
    if (!recording) return selector(...args)

    stats.calls++
    const parent = currentStats
    currentStats = stats
    try {
      return selector(...args)
    } finally {
      currentStats = parent
    }
  }

  // reselect hangs resultFunc, dependencies, clearCache and friends off the selector
  return Object.assign(instrumented, selector) as F
}

const reset = () => {
  for (const stats of statsByDefinition.values()) {
    stats.calls = 0
    stats.equalityChecks = 0
    stats.wastedWalks = 0
    stats.wastedMs = 0
    stats.walkedEntries = 0
  }
}

const rows = () =>
  Array.from(statsByDefinition.values())
    .filter((stats) => stats.calls > 0)
    .sort((left, right) => right.wastedMs - left.wastedMs || right.calls - left.calls)

const report = () => {
  const recorded = rows()
  const wastedMs = recorded.reduce((total, stats) => total + stats.wastedMs, 0)
  const wastedWalks = recorded.reduce((total, stats) => total + stats.wastedWalks, 0)
  const calls = recorded.reduce((total, stats) => total + stats.calls, 0)

  // biome-ignore lint/suspicious/noConsole: this exists to print to the console
  console.log(
    `[glow] "${recordingLabel}": ${calls} selector calls, ${wastedWalks} wasted deep walks over ` +
      `${wastedMs.toFixed(1)} ms across ${recorded.length} selectors`
  )
  // biome-ignore lint/suspicious/noConsole: this exists to print to the console
  console.table(
    recorded.map((stats) => ({
      selector: stats.definedAt,
      source: stats.frame,
      calls: stats.calls,
      'equality checks': stats.equalityChecks,
      'wasted walks': stats.wastedWalks,
      'wasted ms': Number(stats.wastedMs.toFixed(1)),
      'entries walked': stats.walkedEntries
    }))
  )
  return { label: recordingLabel, calls, wastedWalks, wastedMs, selectors: recorded }
}

export const glowSelectorStats = {
  /** Clear counters and start recording. */
  start(label = 'interaction') {
    reset()
    recordingLabel = label
    recording = true
    // biome-ignore lint/suspicious/noConsole: this exists to print to the console
    console.log(`[glow] recording selector stats for "${label}"`)
  },
  /** Print what has been recorded so far and keep going. */
  report,
  /** Print and stop. */
  stop() {
    const result = report()
    recording = false
    return result
  },
  reset,
  /** Raw rows, for asserting on in a test or piping somewhere. */
  snapshot: rows,
  /**
   * Every selector site registered at module load - only the modules the run has imported. Check
   * it once before trusting a recording: if the entries read "unparsed: ...", attribution is
   * broken and the rows are not worth reading yet.
   */
  sites: () => Array.from(statsByDefinition.keys()).sort()
}

/**
 * Import `glowSelectorStats` directly in a benchmark. This only puts it on jsdom's `window` as
 * well, which costs nothing and keeps the two ways of reaching it the same object.
 */
export const registerSelectorStatsConsoleApi = () => {
  if (typeof window === 'undefined') return
  ;(window as unknown as { glowSelectorStats: typeof glowSelectorStats }).glowSelectorStats = glowSelectorStats
}
